import { AxiosError } from 'axios';
import api from './index';

export class ApiError extends Error {
  statusCode?: number;
  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  is_manager: boolean;
  must_change_password?: boolean;
  password_changed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompleteUser extends User {
  cpf?: string;
  phone?: string;
  birthDate?: string;
  departamentId?: number;
  managedDepartamentId?: number;
  addresses?: string[];
  photo?: string | null;
  employee_id?: number;
}

export interface CreateEmployeeAccessData {
  employeeId: number;
  role: 'ADMIN' | 'CLIENT' | 'USER';
  password: string;
}

export interface UpdateEmployeeRoleData {
  employeeId: number;
  role: 'ADMIN' | 'CLIENT' | 'USER';
}

export interface CreateEmployeeWithAccessData {
  name: string;
  email: string;
  cpf: string;
  cargo: string;
  setor?: string;
  department_id?: number | null;
  telefone: string;
  datadeadimissão: string;
  status: 'ativo' | 'inativo' | 'ferias' | 'licenca';
  role: 'ADMIN' | 'CLIENT' | 'USER';
  password: string;
}

export interface AdminResetEmployeePasswordData {
  employeeId: number;
  newPassword: string;
  confirmPassword: string;
  mustChangePassword?: boolean;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface ForceChangePasswordData {
  newPassword: string;
}

export interface EmployeePasswordStatus {
  employeeId: number;
  employeeName: string;
  employeeEmail: string;
  hasAccess: boolean;
  is_active: boolean | null;
  must_change_password: boolean | null;
  password_changed_at: string | null;
}

type ApiErrorResponse = {
  message?: string;
};

export const auth = {
  setAuthToken(token: string | null) {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  },

  async login({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<{ token: string; user: User }> {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user } = response.data;

      return { token: access_token, user };
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Login failed',
          error.response?.status ?? 0,
        );
      } else if (error && typeof error === 'object' && 'request' in error) {
        throw new ApiError('No response from server', 0);
      } else {
        throw new ApiError('Request setup error', 0);
      }
    }
  },

  async refresh(): Promise<{ token: string; user: User }> {
    try {
      const response = await api.post('/auth/refresh');
      const { access_token, user } = response.data;

      return { token: access_token, user };
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao renovar sessao',
          error.response?.status ?? 0,
        );
      }

      throw new ApiError('Request setup error', 0);
    }
  },

  async getSession(): Promise<{
    user: User;
    accessTokenTtlMs: number;
    refreshTokenTtlMs: number;
  }> {
    try {
      const response = await api.get('/auth/session');
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao carregar sessao',
          error.response?.status ?? 0,
        );
      }

      throw new ApiError('Request setup error', 0);
    }
  },

  async createEmployeeAccess({
    employeeId,
    role,
    password,
  }: CreateEmployeeAccessData): Promise<{
    id: number;
    employeeId: number;
    email: string;
    name: string;
    role: string;
  }> {
    try {
      const response = await api.post('/auth/admin/employee-access', {
        employeeId,
        role,
        password,
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao criar perfil de acesso',
          error.response?.status ?? 0,
        );
      } else if (error && typeof error === 'object' && 'request' in error) {
        throw new ApiError('No response from server', 0);
      } else {
        throw new ApiError('Request setup error', 0);
      }
    }
  },

  async updateEmployeeRole({
    employeeId,
    role,
  }: UpdateEmployeeRoleData): Promise<{
    id: number;
    employeeId: number;
    email: string;
    name: string;
    role: string;
  }> {
    try {
      const response = await api.patch(
        `/auth/admin/employee-access/${employeeId}/role`,
        { role },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao atualizar perfil de acesso',
          error.response?.status ?? 0,
        );
      } else if (error && typeof error === 'object' && 'request' in error) {
        throw new ApiError('No response from server', 0);
      } else {
        throw new ApiError('Request setup error', 0);
      }
    }
  },

  async createEmployeeWithAccess(
    data: CreateEmployeeWithAccessData,
  ): Promise<{
    employee: {
      id: number;
      user_id: number;
    };
    access: {
      id: number;
      email: string;
      name: string;
      role: string;
    };
  }> {
    try {
      const response = await api.post('/auth/admin/employees', data);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao criar funcionário com acesso',
          error.response?.status ?? 0,
        );
      } else if (error && typeof error === 'object' && 'request' in error) {
        throw new ApiError('No response from server', 0);
      } else {
        throw new ApiError('Request setup error', 0);
      }
    }
  },

  async changePassword({
    currentPassword,
    newPassword,
  }: ChangePasswordData): Promise<{
    message: string;
    password_changed_at: string;
  }> {
    try {
      const response = await api.post('/auth/password/change', {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao alterar senha',
          error.response?.status ?? 0,
        );
      }
      throw new ApiError('Request setup error', 0);
    }
  },

  async forceChangePassword({
    newPassword,
  }: ForceChangePasswordData): Promise<{
    message: string;
    password_changed_at: string;
  }> {
    try {
      const response = await api.post('/auth/password/force-change', {
        newPassword,
      });
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao confirmar nova senha',
          error.response?.status ?? 0,
        );
      }
      throw new ApiError('Request setup error', 0);
    }
  },

  async adminResetEmployeePassword(
    data: AdminResetEmployeePasswordData,
  ): Promise<{
    employeeId: number;
    userId: number;
    must_change_password: boolean;
  }> {
    try {
      const response = await api.post(
        '/auth/admin/reset-employee-password',
        {
          employeeId: data.employeeId,
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
          mustChangePassword: data.mustChangePassword ?? true,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao redefinir senha do funcionário',
          error.response?.status ?? 0,
        );
      }
      throw new ApiError('Request setup error', 0);
    }
  },

  async getEmployeePasswordStatus(
    employeeId: number,
  ): Promise<EmployeePasswordStatus> {
    try {
      const response = await api.get(
        `/auth/admin/employee-password-status/${employeeId}`,
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        throw new ApiError(
          (error.response?.data as ApiErrorResponse | undefined)?.message ||
            'Falha ao carregar status da senha',
          error.response?.status ?? 0,
        );
      }
      throw new ApiError('Request setup error', 0);
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Always clear local client state even if the server session is gone.
    }
    this.setAuthToken(null);
  },
};
